import { compact, get, head, isArray } from 'lodash/fp';
import { useSelector } from 'react-redux';
import React from 'react';
import type { GetServerSideProps } from 'next';

import { EXPLAINED_TERMS_SLUG_BLOCKLIST } from '../utils/is-in-explained-blocklist';
import { ExplainedTerm, Offer, Placeholder } from '../../src/client/types/__codegen__/sanity/graphql';
import { FRAGMENTS, SLUG } from '../../src/client/models/table-of-contents/constants';
import ConsiderationLandingPage from '../../src/client/pages/consideration-landing';
import filterOffersByProductType from '../../src/client/utils/filter-offers-by-product-type';
import GET_EXPLAINED_TERM from '../../src/client/pages/explained-term/detail/queries/get-explained-term';
import getCmsData from '../../src/client/cms/utils/get-cms-data';
import getContentToRender from '../../src/client/pages/consideration-landing/utils/get-content-to-render';
import getOffersContent from '../utils/get-offers-content';
import getShouldSkipClientSideRequest from '../../src/client/utils/get-should-skip-client-side-request';
import getTableOfContentsListByIdentifierQuery from '../../src/client/models/table-of-contents/utils/get-table-of-contents-list-by-identifier-query';
import selectCustomer from '../../src/client/selectors/select-customer';
import selectHasPartnerReferrer from '../../src/client/selectors/select-has-partner-referrer';
import selectTargetSegment from '../../src/client/selectors/select-target-segment';
import setCacheControlHeader from '../utils/set-cache-control-header';
import useGetExplainedTerm from '../../src/client/pages/explained-term/detail/utils/use-get-explained-term';
import useGetOffersContent from '../utils/use-get-offers-content';
import useTableOfContents from '../../src/client/cms/utils/use-table-of-contents';
import type { TableOfContentsItem, TableOfContentsListType } from '../../src/client/types/sanityInternal';

const slug = EXPLAINED_TERMS_SLUG_BLOCKLIST.HEALTH_INSURANCE_LANDING_PAGE;
const contactTitle = 'Need help choosing a cover?';

export type HealthInsurancePageProps = {
  activeOffers: Offer[];
  placeholders: Placeholder[];
  serverSideData: {
    contact: {
      items: TableOfContentsItem[];
      name: string;
    };
    explainedTerm: ExplainedTerm;
  };
};

const HealthInsurancePage = ({
  activeOffers: serverSideOffers,
  placeholders: serverSidePlaceholders,
  serverSideData,
}: HealthInsurancePageProps) => {
  const shouldSkip = getShouldSkipClientSideRequest();
  const { activeOffers, placeholders, loading: isOfferContentLoading } = useGetOffersContent({ shouldSkip });

  // Refetch content for time travel?

  const { items: contactItems, loading: isLoadingContact } = useTableOfContents({
    fragment: FRAGMENTS.CONTACTS,
    skip: shouldSkip,
    slug: SLUG.LOOKING_TO_JOIN_CONTACTS,
  });

  const { explainedTerm: explainedTermData, isLoading: isLoadingExplainedTerm } = useGetExplainedTerm({
    skip: shouldSkip,
    slug,
  });

  const isPartnerReferral = useSelector(selectHasPartnerReferrer);
  const customer = useSelector(selectCustomer);
  const targetSegment = useSelector(selectTargetSegment);

  const isLoading = isLoadingExplainedTerm || isLoadingContact;

  const clientSideData = {
    contact: {
      items: contactItems as TableOfContentsItem[],
      name: contactTitle,
    },
    explainedTerm: explainedTermData,
  };

  const cmsData = isLoading || shouldSkip ? serverSideData : clientSideData;

  const offersContent =
    shouldSkip || isOfferContentLoading
      ? {
          activeOffers: serverSideOffers,
          placeholders: serverSidePlaceholders,
        }
      : {
          activeOffers,
          placeholders,
        };

  const filteredOffer = filterOffersByProductType(customer?.productType, offersContent.activeOffers) as Offer;

  const offerProps = {
    overrideOffers: compact([filteredOffer]),
    placeholders: offersContent.placeholders,
    targetSegment,
  };

  const offerContentToRender = filteredOffer && !isPartnerReferral ? offerProps : null;
  const considerationContent = getContentToRender({ cmsData, offerContentToRender });

  return <ConsiderationLandingPage content={considerationContent} />;
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  setCacheControlHeader(context.res);

  const contactsTableOfContentsQuery = getTableOfContentsListByIdentifierQuery(FRAGMENTS.CONTACTS);
  const [explainedTermData, contactData, { activeOffers, placeholders }] = await Promise.all([
    getCmsData(GET_EXPLAINED_TERM, { variables: { slug } }),
    getCmsData(contactsTableOfContentsQuery, {
      variables: {
        identifier: SLUG.LOOKING_TO_JOIN_CONTACTS,
      },
    }),
    getOffersContent(),
  ]);

  const explainedTerm = (head(get('allExplainedTerm', explainedTermData)) || null) as ExplainedTerm;
  const contactList = (head(get('allTableOfContentsList', contactData)) || null) as TableOfContentsListType;
  const items = isArray(contactList?.items) ? contactList?.items : [];
  const cmsData = {
    contact: {
      items,
      name: contactTitle,
    },
    explainedTerm,
  };

  return {
    props: {
      activeOffers,
      placeholders,
      serverSideData: cmsData,
    },
  };
};

export default HealthInsurancePage;
